/**
 * Zod mirror of `types/contract.ts`.
 *
 * Two schemas, matching the two failure surfaces:
 *  - `ManuscriptContractShapeSchema` is shallow -- just enough to know there is
 *    a document to render at all. A failure here is fatal (no blocks array
 *    means nothing can be partially rendered), thrown by `renderContract` and
 *    caught by `DocumentWorkspaceErrorBoundary`.
 *  - `BlockSchema` (and `ManuscriptContractSchema`, its whole-contract
 *    counterpart) is deep. It is deliberately NOT used to gate the whole
 *    render: `renderContract` validates one block at a time so a single bad
 *    block degrades to an inline placeholder instead of blanking the document.
 */
import { z } from 'zod';

export const ContentFormatSchema = z.enum(['md', 'html', 'latex']);

export const BlockContentSchema = z.object({
  type: ContentFormatSchema,
  value: z.string(),
});

export const BlockTypeSchema = z.enum([
  'paragraph',
  'section',
  'table',
  'figure',
  'formula',
]);

export const BlockRoleSchema = z.enum([
  'article-title',
  'authors',
  'affiliations',
  'abstract',
  'ethics-approval',
  'corresponding-authors',
  'equal-contribution-note',
  'running-title',
  'keywords',
  'funding',
  'conflict-of-interest',
  'acknowledgements',
  'author-contributions',
  'data-availability',
  'ai-use-statement',
  'supplementary-material',
  'abbreviations',
  'references',
]);

export const BlockCommentSchema = z.object({
  commentId: z.string(),
  author: z.string(),
  authorRole: z.enum(['reviewer', 'editor', 'author']),
  quote: z.string(),
  text: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  resolvedBy: z.string().optional(),
  createdAt: z.string(),
});

export const BlockSchema = z.object({
  type: BlockTypeSchema.nullable(),
  role: BlockRoleSchema.nullable(),
  parent: z.string().nullable(),
  title: z.string().nullable(),
  content: BlockContentSchema.nullable(),

  label: z.string().optional(),
  caption: z.string().optional(),
  href: z.string().optional(),
  alt: z.string().nullable().optional(),
  mathml: z.string().optional(),

  id: z.string().optional(),
  comments: z.array(BlockCommentSchema).optional(),
});

export const ContractSourceSchema = z.object({
  document: z.string(),
  package: z.string().optional(),
  assets: z.array(z.object({ path: z.string(), url: z.string() })).optional(),
});

/**
 * Shallow envelope check: enough to know there's a document to render.
 *
 * Only `blocks` is required -- a missing blocks array means nothing can be
 * partially rendered, so that alone is fatal. Every other field is metadata
 * consumed elsewhere (media-access, event payloads) but not by rendering
 * itself, and hosts have been observed sending contracts with only `blocks`
 * populated. Rejecting those here would blank the whole document over fields
 * `renderContract` never actually reads.
 *
 * `version`/`revision` accept string or number: the extraction service emits
 * `revision` as a number and `version` as a number too (see
 * `extracted_doc.json`), diverging from `types/contract.ts`'s declared
 * `string`/`number`; some flows also deliberately probe with a malformed
 * `revision` string to test their own fallback (see
 * `DocumentViewer.test.tsx`).
 */
export const ManuscriptContractShapeSchema = z.object({
  schemaVersion: z.string().optional(),
  submissionId: z.string().optional(),
  articleType: z.string().optional(),
  lang: z.string().optional(),
  extractedAt: z.string().optional(),
  revision: z.union([z.string(), z.number()]).optional(),
  version: z.union([z.string(), z.number()]).optional(),
  source: ContractSourceSchema.optional(),
  blocks: z.array(z.unknown()),
});

/** Full depth, block-by-block. Not used to gate rendering -- see file header. */
export const ManuscriptContractSchema = ManuscriptContractShapeSchema.extend({
  blocks: z.array(BlockSchema),
});

/** Joins the first few zod issues into one readable line. */
export function formatZodError(error: z.ZodError, limit = 3): string {
  return error.issues
    .slice(0, limit)
    .map((issue) => {
      const path = issue.path.length ? issue.path.join('.') : '(root)';

      return `${path}: ${issue.message}`;
    })
    .join('; ');
}
